import { Box, Button, Flex, Icon, IconButton, Text, Textarea } from '@chakra-ui/react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { LuX } from 'react-icons/lu';

import {
  ADVISOR_MESSAGE_FEEDBACK_COMMENT_MAX_LENGTH,
  ADVISOR_PANEL_BOX_SHADOW,
  ADVISOR_PANEL_NEUTRAL_100,
  ADVISOR_PANEL_ON_PRIMARY,
  ADVISOR_PANEL_PRIMARY_500,
} from './advisor-panel-tokens';

/**
 * Canonical **inline “What went wrong?”** card for thumbs-down + optional written feedback.
 * **Single source of truth** for Figma parity (Kin Chatbot — “Not helpful” flow, e.g. frame
 * [`13191:23818`](https://www.figma.com/design/bYyIivHby22hQ1jTAxVnHV/Kin-Chatbot?node-id=13191-23818)).
 * **Do not re‑implement this markup in `frontend/` or `frontend-partner/`** — both shells must
 * import this component so the surface never drifts between core product and partner.
 * Pair with {@link useAgentAssistantMessageFeedback} using `thumbDownMode: "intent"` and
 * `onThumbDownIntent` to open the card; call `confirmThumbDown` from Skip / Submit handlers.
 */
export interface AgentAssistantWrittenFeedbackInlineProps {
  draft: string;
  onDraftChange: (value: string) => void;
  disabled: boolean;
  pending: 'skip' | 'submit' | null;
  onRequestClose: () => void;
  onSkip: () => void | Promise<void>;
  onSubmit: () => void | Promise<void>;
}

export function AgentAssistantWrittenFeedbackInline({
  draft,
  onDraftChange,
  disabled,
  pending,
  onRequestClose,
  onSkip,
  onSubmit,
}: AgentAssistantWrittenFeedbackInlineProps) {
  return (
    <Box
      w="100%"
      bg="white"
      borderWidth="1px"
      borderColor={ADVISOR_PANEL_NEUTRAL_100}
      borderRadius="8px"
      boxShadow={ADVISOR_PANEL_BOX_SHADOW}
      px={4}
      pt={3}
      pb={4}
      onKeyDown={(e: ReactKeyboardEvent) => {
        if (e.key !== 'Escape') return;
        e.preventDefault();
        e.stopPropagation();
        if (pending) return;
        onRequestClose();
      }}
    >
      <Flex align="center" justify="space-between" gap={2} mb={3}>
        <Text fontSize="md" fontWeight="semibold" color="fg">
          What went wrong?
        </Text>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label="Close written feedback"
          disabled={disabled || !!pending}
          onClick={() => {
            if (pending) return;
            onRequestClose();
          }}
        >
          <Icon as={LuX} boxSize={4} />
        </IconButton>
      </Flex>
      <Textarea
        value={draft}
        onChange={e => onDraftChange(e.target.value)}
        aria-label="Optional written feedback on what went wrong with this reply"
        placeholder="Describe what seems off (optional)"
        rows={4}
        maxLength={ADVISOR_MESSAGE_FEEDBACK_COMMENT_MAX_LENGTH}
        disabled={disabled || !!pending}
        bg="white"
        borderColor={ADVISOR_PANEL_NEUTRAL_100}
        borderRadius="2px"
        fontSize="sm"
        color="fg.muted"
        mb={4}
        _placeholder={{ color: 'fg.subtle' }}
      />
      <Flex gap={2} w="100%" justify="flex-end">
        <Button
          variant="ghost"
          colorPalette="neutral"
          minW="61px"
          disabled={disabled || !!pending}
          loading={pending === 'skip'}
          onClick={() => {
            void onSkip();
          }}
        >
          Skip
        </Button>
        <Button
          backgroundColor={ADVISOR_PANEL_PRIMARY_500}
          color={ADVISOR_PANEL_ON_PRIMARY}
          minW="120px"
          _hover={{ bg: ADVISOR_PANEL_PRIMARY_500, opacity: 0.92 }}
          loading={pending === 'submit'}
          disabled={disabled || !!pending}
          onClick={() => {
            void onSubmit();
          }}
        >
          Submit
        </Button>
      </Flex>
    </Box>
  );
}
